import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { StepHelpers } from '@shared/components/Stepper';
import { Button } from '@shared/ui/button';
import { toast } from '@shared/hooks/use-toast';
import { DynamicForm } from '@shared/components/DynamicForm';
import { FormField, FormService } from '@shared/services/FormServices';
import { FIELD_CLASSES } from '@shared/utils/constants';
import { PageHeader } from '@shared/components/PageHeader';

interface SetNewValuesProps {
  storageKey?: string;
  logTimeUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

export const SetNewValues: React.FC<SetNewValuesProps> = ({
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating
}) => {
  const dataLoadedRef = useRef(false);

  // Read cross-step data
  const step1Data = stepHelpers?.getStepData?.(0) || { selectedRecords: [] };
  const step2Data = stepHelpers?.getStepData?.(1) || { fieldsToEdit: {} };
  const selectedRecords = step1Data.selectedRecords || [];
  const fieldsToEdit = step2Data.fieldsToEdit || {};
  const commonRateType = step2Data.commonRateType || null;

  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step data via stepHelpers:", error);
        toast({
          title: "Failed to save progress",
          description: "Your progress may not be saved. Please try again.",
          variant: "destructive",
        });
      }
    },
    [stepHelpers, currentStepIndex]
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data via stepHelpers:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Build dynamic form fields based on what user toggled in Step 2
  const dynamicFields: FormField[] = useMemo(() => {
    const result: FormField[] = [];

    if (fieldsToEdit.effectiveFrom) {
      result.push({
        name: "effectiveFrom",
        label: "Effective From",
        type: "datepicker",
        placeholder: "dd/mm/yyyy",
        group: "Set New Values",
        helperText: "When the records start applying after the bulk change.",
        groupHelperText:"Enter the values that will be applied to all selected records.",
        classes: FIELD_CLASSES,
        className: "col-span-12 md:col-span-6",
        required: true,
      });
    }

    if (fieldsToEdit.endDate) {
      result.push({
        name: "endDate",
        label: "End Date",
        type: "datepicker",
        placeholder: "dd/mm/yyyy",
        group: "Set New Values",
        helperText: "When the records stop applying.",
        classes: FIELD_CLASSES,
        className: "col-span-12 md:col-span-6",
        required: true,
      });
    }

    if (fieldsToEdit.rateStructure) {
      const rateGroupName = `Rate Adjustment — ${commonRateType || ''}`;
      
      result.push({
        name: "direction",
        label: "Direction",
        type: "select",
        isSearchable: false,
        group: rateGroupName,
        groupHelperText: `Adjust all rate components across ${selectedRecords.length} records.`,
        helperText: "Choose to increase or decrease the current rates.",
        options: [
          { label: "Increase", value: "increase" },
          { label: "Decrease", value: "decrease" },
        ],
        classes: FIELD_CLASSES,
        className: "col-span-12 md:col-span-4",
        required: true,
      });
      
      result.push({
        name: "adjustmentType",
        label: "Adjustment Type",
        type: "select",
        isSearchable: false,
        group: rateGroupName,
        helperText: "Select whether the change is a percentage or a flat amount.",
        options: [
          { label: "Percentage", value: "percentage" },
          { label: "Flat Amount", value: "flat" },
        ],
        classes: FIELD_CLASSES,
        className: "col-span-12 md:col-span-4",
        required: true,
      });

      result.push({
        name: "adjustmentValuePercentage",
        label: "Percentage (%)",
        type: "decimal",
        placeholder: "e.g. 5.00",
        required: true,
        group: rateGroupName,
        helperText: "Enter the percentage value to apply.",
        showWhen: (values) => values.adjustmentType === 'percentage',
        classes: FIELD_CLASSES,
        className: "col-span-12 md:col-span-4",
      });

      result.push({
        name: "adjustmentValueFlat",
        label: "Flat Amount",
        type: "decimal",
        placeholder: "e.g. 0.05",
        required: true,
        group: rateGroupName,
        helperText: "Enter the fixed amount to adjust by.",
        showWhen: (values) => values.adjustmentType === 'flat',
        classes: FIELD_CLASSES,
        className: "col-span-12 md:col-span-4",
      });
    }

    return result;
  }, [fieldsToEdit, commonRateType, selectedRecords.length]);

  const form = FormService.createForm({
    fields: dynamicFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      direction: 'increase',
      adjustmentType: 'percentage',
    },
  });

  const hasAnyField = Object.values(fieldsToEdit).some(Boolean);

  // Restore saved data on mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          // Restore dynamic form values
          const { rateAdjustment, ...formValues } = savedStepData;
          let resetValues = { ...formValues };

          // Restore rate adjustment state
          if (rateAdjustment) {
            resetValues.direction = rateAdjustment.direction;
            resetValues.adjustmentType = rateAdjustment.adjustmentType;
            if (rateAdjustment.adjustmentType === 'percentage') {
              resetValues.adjustmentValuePercentage = rateAdjustment.adjustmentValue;
            } else {
              resetValues.adjustmentValueFlat = rateAdjustment.adjustmentValue;
            }
          }
          
          form.reset(resetValues);
        }
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getStepData, form]);

  const handleContinue = async () => {
    if (clearValidationError) {
      clearValidationError();
    }

    let values: any = {};

    if (dynamicFields.length > 0) {
      const isValid = await form.trigger();
      if (!isValid) {
        if (setValidationError) {
          setValidationError("Please fill in all required fields.");
        }
        return;
      }
      values = { ...form.getValues() };
    }

    if (fieldsToEdit.rateStructure) {
      values.rateAdjustment = {
        direction: values.direction,
        adjustmentType: values.adjustmentType,
        adjustmentValue: values.adjustmentType === 'percentage' 
          ? values.adjustmentValuePercentage 
          : values.adjustmentValueFlat
      };
    }

    saveStepData({
      ...values,
      isStepComplete: true,
      completedAt: new Date().toISOString(),
    });

    if (logTimeUntilStep) {
      logTimeUntilStep("Set New Values");
    }

    if (onNext) onNext();
  };

  if (!hasAnyField) {
    return (
      <div className="space-y-6">
        <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm text-center">
          <p className="text-gray-500">No fields were selected in the previous step. Please go back and choose at least one field to edit.</p>
        </div>

        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={onPrevious}
            disabled={isValidating}
          >
            Previous
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Dynamic form fields */}
      {dynamicFields.length > 0 && (
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">

          <DynamicForm
            fields={dynamicFields}
            form={form}
            config={{
              showSubmitButton: false,
              showResetButton: false,
            }}
            gridColumns={12}
            responsiveColumns={{ sm: 12, md: 12 }}
          />
        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onPrevious}
          disabled={isValidating}
        >
          Previous
        </Button>
        <Button
          onClick={handleContinue}
          disabled={isValidating}
          className="min-w-[120px]"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};
